import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Flex,
  Typography,
  message,
} from "antd";
import { CheckOutlined, EditOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_QUOTES, API_URL_USER_ADDRESSES_V1 } from "@/services/XCONS_ENV";
import { isValidCuit, isValidDni, realValue } from "../utils/quoteValidations";

const { Text } = Typography;

export const BILLING_TYPES = [
  { value: "1", label: "B Consumidor Final" },
  { value: "2", label: "A Monotributo" },
  { value: "3", label: "B Empresa IVA Exento" },
  { value: "4", label: "A Empresa Responsable Inscripto" },
];

// Types 2, 3, 4 require company fields; 1 only needs name + ID
const COMPANY_TYPES = ["2", "3", "4"];

interface BillingDataModalProps {
  open: boolean;
  onClose: () => void;
  quoteId: number;
  billingAddress: any;
  /** Owner userId; required to persist into the user's agenda. */
  userId?: number | null;
  /** Quote's storeCode; persisted alongside the agenda row so it shows up in the contact's address book filtered by store. */
  storeCode?: string | null;
  onSave: () => void;
  /** Opens the owner's billing agenda. Shown as a button in the drawer title when present. */
  onOpenAgenda?: () => void;
}

export const BillingDataModal: React.FC<BillingDataModalProps> = ({
  open,
  onClose,
  quoteId,
  billingAddress,
  userId,
  storeCode,
  onSave,
  onOpenAgenda,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [addToAgenda, setAddToAgenda] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const billingType = Form.useWatch("billingType", form);

  const isCompanyType = COMPANY_TYPES.includes(billingType);

  const hasSavedBilling = Boolean(
    billingAddress?.company || billingAddress?.firstname || billingAddress?.vatId,
  );

  // Pre-fill form when drawer opens
  useEffect(() => {
    if (!open) return;
    setIsEditing(true);
    setAddToAgenda(false);
    setHasChanges(false);
    const addr = billingAddress;
    const bt = addr?.billingType ? String(addr.billingType) : "1";
    const isCompany = COMPANY_TYPES.includes(bt);
    const fullName = addr
      ? `${addr.firstname || ""} ${addr.lastname || ""}`.trim() || addr.company || ""
      : "";

    form.setFieldsValue({
      billingType: bt,
      fullName: !isCompany ? fullName : "",
      documentId: !isCompany ? addr?.vatId || "" : "",
      companyName: isCompany ? addr?.company || "" : "",
      taxId: isCompany ? addr?.vatId || "" : "",
      // El domicilio fiscal vive en `street1` del snapshot: `taxDirectorate` no existe
      // en la API, y leerlo de ahí dejaba el campo vacío en cada apertura (#378).
      taxDirectorate: isCompany ? realValue(addr?.street1) : "",
      city: addr?.cityName || "",
      state: addr?.provinceName || "",
      zipCode: addr?.postcode || "",
    });
  }, [open, billingAddress, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const bt = Number(values.billingType);
      const isCompany = COMPANY_TYPES.includes(values.billingType);

      let payload: Record<string, any> = {
        type: "billing",
        billingType: bt,
      };

      if (isCompany) {
        payload = {
          ...payload,
          company: values.companyName || "",
          vatId: values.taxId || "",
          street1: (values.taxDirectorate || "").trim(),
          cityName: values.city || "",
          provinceName: values.state || "",
          postcode: values.zipCode || "",
        };
      } else {
        const nameParts = (values.fullName || "").trim().split(/\s+/);
        const firstname = nameParts[0] || "";
        const lastname = nameParts.slice(1).join(" ") || "";
        // Consumer final: los campos de dirección no se piden al usuario,
        // pero se envían con "-" para pasar validaciones downstream (Magento checkout).
        payload = {
          ...payload,
          company: (values.fullName || "").trim(),
          vatId: values.documentId || "",
          firstname,
          lastname,
          cityName: "-",
          provinceName: "-",
          postcode: "-",
        };
      }

      await axiosInstance.patch(`${API_URL_QUOTES}/${quoteId}/addresses`, payload);

      if (addToAgenda) {
        if (!userId) {
          message.warning(
            "Datos guardados en el presupuesto. El presupuesto no tiene dueño asignado: no se pudo agregar a la agenda.",
          );
        } else {
          // The agenda billing row mirrors the snapshot payload. The new
          // schema uses billingType:int (no separate billingTypeId) and the
          // street/city columns share the snapshot naming.
          const agendaPayload: Record<string, any> = {
            type: "billing",
            userId,
            store: storeCode || null,
            countryId: "AR",
            company: payload.company || "",
            vatId: payload.vatId || "",
            firstname: payload.firstname || "",
            lastname: payload.lastname || "",
            cityName: payload.cityName || "",
            provinceName: payload.provinceName || "",
            postcode: payload.postcode || "",
            billingType: bt,
            // Required by the entity NOT NULL columns; keep dashes for
            // consumer-final billings that don't have a real shipping line.
            street1: payload.street1 || "-",
          };
          try {
            await axiosInstance.post(API_URL_USER_ADDRESSES_V1, agendaPayload);
          } catch (e: any) {
            console.error("[agenda billing] POST failed", {
              payload: agendaPayload,
              response: e?.response?.data,
              status: e?.response?.status,
            });
            const apiMsg =
              e?.response?.data?.message || e?.response?.data?.error;
            const detail = Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg;
            message.warning(
              detail
                ? `Datos guardados en el presupuesto, pero no se pudieron agregar a la agenda: ${detail}`
                : "Datos guardados en el presupuesto, pero no se pudieron agregar a la agenda.",
            );
          }
        }
      }

      message.success("Datos de facturación actualizados");
      onSave();
    } catch (err: any) {
      if (err?.errorFields) return; // antd validation errors shown inline
      message.error("Error al guardar datos de facturación");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <span>Datos de facturación</span>
          {userId && onOpenAgenda && (
            <span
              onClick={() => onOpenAgenda()}
              style={{ cursor: "pointer", display: "inline-flex", lineHeight: 0 }}
              title="Buscar en la agenda del dueño"
            >
              <svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter_billing_title)">
                  <mask id="mask_billing_title" fill="white"><path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"/></mask>
                  <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="white" shapeRendering="crispEdges"/>
                  <path d="M4 0V1H20V0V-1H4V0ZM24 4H23V20H24H25V4H24ZM20 24V23H4V24V25H20V24ZM0 20H1V4H0H-1V20H0ZM4 24V23C2.34315 23 1 21.6569 1 20H0H-1C-1 22.7614 1.23858 25 4 25V24ZM24 20H23C23 21.6569 21.6569 23 20 23V24V25C22.7614 25 25 22.7614 25 20H24ZM20 0V1C21.6569 1 23 2.34315 23 4H24H25C25 1.23858 22.7614 -1 20 -1V0ZM4 0V-1C1.23858 -1 -1 1.23858 -1 4H0H1C1 2.34315 2.34315 1 4 1V0Z" fill="#D9D9D9" mask="url(#mask_billing_title)"/>
                  <path d="M15.1178 15.7647L15.6069 8.85822C15.6114 8.79575 15.6024 8.73306 15.5805 8.67439C15.5586 8.61572 15.5243 8.56243 15.4801 8.51815C15.4358 8.47387 15.3825 8.43963 15.3238 8.41774C15.2652 8.39585 15.2025 8.38682 15.14 8.39128L8.23452 8.88142C8.21369 8.88287 8.1937 8.89029 8.17696 8.90278C8.16021 8.91526 8.14741 8.9323 8.14008 8.95186C8.13274 8.97142 8.13118 8.99268 8.13559 9.0131C8.13999 9.03352 8.15017 9.05224 8.16492 9.06704L8.94798 9.8501C8.99245 9.89457 9.05433 9.91777 9.11813 9.91391L13.6734 9.59101L8.22969 15.0348C8.18715 15.0773 8.18715 15.1469 8.22969 15.1894L8.80974 15.7695C8.85228 15.812 8.92188 15.812 8.96442 15.7695L14.4082 10.3257L14.0853 14.8811C14.0804 14.9439 14.1036 15.0058 14.1491 15.0512L14.9322 15.8343C14.9979 15.9 15.111 15.8584 15.1178 15.7647Z" fill="black" fillOpacity="0.88"/>
                </g>
                <defs>
                  <filter id="filter_billing_title" x="0" y="0" width="24" height="26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="2"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_billing_title"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_billing_title" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </span>
          )}
        </Flex>
      }
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        isEditing ? (
          <Flex justify="space-between" align="center">
            {(() => {
              const agendaDisabled = !userId || (hasSavedBilling && !hasChanges);
              const agendaWarning = !userId
                ? "El presupuesto necesita dueño para poder agregar los datos de facturación a una agenda"
                : "Los datos ya están guardados en la agenda";
              return (
                <Flex
                  align="center"
                  gap={8}
                  style={{ cursor: agendaDisabled ? "not-allowed" : "default" }}
                  onClick={() => {
                    if (agendaDisabled) message.warning(agendaWarning);
                  }}
                >
                  <Text style={{ fontSize: 13, opacity: agendaDisabled ? 0.4 : 1 }}>Agregar a la agenda</Text>
                  <Switch
                    checked={addToAgenda}
                    onChange={setAddToAgenda}
                    size="small"
                    disabled={agendaDisabled}
                  />
                </Flex>
              );
            })()}
            <Flex gap={8}>
              <Button
                onClick={hasSavedBilling ? () => setIsEditing(false) : onClose}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                loading={saving}
                onClick={handleSave}
                icon={<CheckOutlined />} iconPosition="end"
              >
                Guardar
              </Button>
            </Flex>
          </Flex>
        ) : null
      }
    >
      {!isEditing ? (
        <Flex vertical gap={12}>
          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {BILLING_TYPES.find(
                (t) => t.value === String(billingAddress?.billingType ?? "1"),
              )?.label || "—"}
            </Text>
            <Text strong>
              {billingAddress?.company ||
                `${billingAddress?.firstname || ""} ${billingAddress?.lastname || ""}`.trim() ||
                "—"}
            </Text>
            {billingAddress?.vatId && (
              <Text>{billingAddress.vatId}</Text>
            )}
            {COMPANY_TYPES.includes(String(billingAddress?.billingType)) &&
              realValue(billingAddress?.street1) && (
                <Text>{realValue(billingAddress?.street1)}</Text>
              )}
            {(billingAddress?.cityName ||
              billingAddress?.provinceName ||
              billingAddress?.postcode) && (
              <Text type="secondary">
                {[
                  billingAddress?.cityName,
                  billingAddress?.provinceName,
                  billingAddress?.postcode
                    ? `(${billingAddress.postcode})`
                    : null,
                ]
                  .filter(Boolean)
                  .filter((v) => v !== "-")
                  .join(", ")}
              </Text>
            )}
          </Flex>

          <Flex justify="end">
            <Button
              shape="round"
              icon={<EditOutlined />}
              iconPosition="end"
              onClick={() => setIsEditing(true)}
            >
              Editar
            </Button>
          </Flex>
        </Flex>
      ) : (
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ billingType: "1" }}
        onValuesChange={() => setHasChanges(true)}
      >
        <Form.Item
          label="Tipo de factura"
          name="billingType"
          rules={[{ required: true, message: "Seleccione tipo de factura" }]}
        >
          <Select options={BILLING_TYPES} />
        </Form.Item>

        {/* Consumer Final (1, 5): Name + ID */}
        {!isCompanyType && (
          <Flex gap={16}>
            <Form.Item
              label="Nombre y apellido"
              name="fullName"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Input placeholder="Nombre y apellido" />
            </Form.Item>
            <Form.Item
              label="DNI"
              name="documentId"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Requerido" },
                {
                  // La API rechaza el pedido con otro formato; mejor marcarlo acá (#378).
                  validator: (_, value) =>
                    !value || isValidDni(value)
                      ? Promise.resolve()
                      : Promise.reject("El DNI tiene que tener 7 u 8 dígitos"),
                },
              ]}
            >
              <Input placeholder="Ingresá el DNI" />
            </Form.Item>
          </Flex>
        )}

        {/* Company types (2, 3, 4): Company + Tax ID + address fields */}
        {isCompanyType && (
          <>
            <Flex gap={16}>
              <Form.Item
                label="Razón social"
                name="companyName"
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Razón social" />
              </Form.Item>
              <Form.Item
                label="CUIT"
                name="taxId"
                style={{ flex: 1 }}
                rules={[
                  { required: true, message: "Requerido" },
                  {
                    validator: (_, value) =>
                      !value || isValidCuit(value)
                        ? Promise.resolve()
                        : Promise.reject("El CUIT tiene que tener 11 dígitos"),
                  },
                ]}
              >
                <Input placeholder="Ingresá el CUIT" />
              </Form.Item>
            </Flex>
            <Flex gap={16}>
              <Form.Item
                label="Domicilio fiscal"
                name="taxDirectorate"
                style={{ flex: 1 }}
                rules={[{ required: true, whitespace: true, message: "Requerido" }]}
              >
                <Input placeholder="Ingresá el domicilio fiscal" />
              </Form.Item>
              <Form.Item
                label="Ciudad"
                name="city"
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ingresá la ciudad" />
              </Form.Item>
            </Flex>
            <Flex gap={16}>
              <Form.Item
                label="Provincia"
                name="state"
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ingresá la provincia" />
              </Form.Item>
              <Form.Item
                label="Código postal"
                name="zipCode"
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Código postal" />
              </Form.Item>
            </Flex>
          </>
        )}
      </Form>
      )}
    </Drawer>
  );
};
